// @ts-nocheck
/**
 * TopBar Component
 *
 * Desktop-only top navigation bar with home button and utility icons.
 * Features:
 * - App Launcher (waffle menu)
 * - Home button with icon
 * - Utility icons: Admin Editor, Notifications, Settings, Help, Logout
 * - Force desktop view toggle (developer mode)
 * - 32px height
 *
 * Note: Breadcrumb navigation and "Manage Campaigns" button have been moved
 * to PhaseSelector component for better organization.
 *
 * Design: Mobile-first (hidden on mobile, visible on desktop md:flex)
 */

import React, { useState } from 'react';
import { Home, Settings, HelpCircle, LogOut, Monitor, Bug, Maximize2, Minimize2, MessageCircle } from 'lucide-react';
import { useUIStore } from '@shared/store/uiStore';
import { DevLogPanel } from '@shared/components/ui/DevLogPanel';
import { AdminEditorModal } from '@features/admin/components';
import { AppTab } from '../../../../types';
import { NotificationButton } from '../notifications/NotificationButton';
import { AppLauncher } from './AppLauncher';
import { DashboardSearchBar } from '@pages/dashboard/components/DashboardSearchBar';

export interface TopBarProps {
  /** Currently active tab (kept for compatibility, no longer used for breadcrumb) */
  activeTab: AppTab;
  /** @deprecated - Now handled by BreadcrumbCampaignSelector via campaign store */
  campaignName?: string;
  /** Callback when settings button is clicked */
  onOpenSettings: () => void;
  /** Callback when logout button is clicked */
  onLogout: () => void;
  /** Callback when home button is clicked */
  onNavigateHome: () => void;
  /** Callback when AI assistant button is clicked */
  onNavigateAssistant?: () => void;
  /** @deprecated - Moved to PhaseSelector component */
  onNavigateToCampaigns?: () => void;
  /** Whether force desktop view is enabled (developer mode) */
  forceDesktopView?: boolean;
  /** Callback when desktop view toggle is clicked */
  onToggleDesktopView?: () => void;
}

export const TopBar: React.FC<TopBarProps> = ({
  activeTab,
  // campaignName - deprecated, now handled by BreadcrumbCampaignSelector
  // onNavigateToCampaigns - deprecated, moved to PhaseSelector
  onOpenSettings,
  onLogout,
  onNavigateHome,
  onNavigateAssistant,
  forceDesktopView = false,
  onToggleDesktopView,
}) => {
  // State for Admin Editor Modal
  const [isAdminEditorOpen, setIsAdminEditorOpen] = useState(false);
  // State for Dev Log Panel
  const [isDevLogOpen, setIsDevLogOpen] = useState(false);
  // Focus mode from UI store
  const { focusMode, toggleFocusMode } = useUIStore();
  const isAssistantRoute = window.location.pathname.startsWith('/app/assistant');

  return (
    <>
    <div className="hidden md:flex h-10 bg-gradient-to-r from-dark-surface/60 via-dark-bg/80 to-dark-surface/40 backdrop-blur-sm border-b border-dark-border/25 items-center px-2 md:px-4 shrink-0">
      {/* Left Section: App Launcher, Home, Focus Mode */}
      <div className="flex items-center">
        {/* App Launcher (Waffle Menu) */}
        <AppLauncher className="mr-2" />

        {/* Home Button */}
        <span
          onClick={onNavigateHome}
          className="flex items-center gap-1 text-brand-500 cursor-pointer text-xs"
        >
          <Home size={14} /> <span className="hidden sm:inline">Home</span>
        </span>

        {/* Focus Mode Toggle (hide/show both sidebars) */}
        <span
          onClick={toggleFocusMode}
          className={`flex items-center gap-1 cursor-pointer text-xs ml-3 transition-all duration-150 ${
            focusMode
              ? 'text-brand-400 hover:text-brand-300'
              : 'text-dark-muted hover:text-dark-text'
          }`}
          title={focusMode ? 'Exit focus mode (show panels)' : 'Enter focus mode (hide panels)'}
          data-testid="focus-mode-toggle"
        >
          {focusMode ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          <span className="hidden sm:inline">{focusMode ? 'Exit Focus' : 'Focus'}</span>
        </span>
      </div>

      {/* Center Section: Global Search Bar - wider and centered */}
      <div className="flex-1 flex justify-center px-4">
        <DashboardSearchBar compact className="w-full max-w-xl" />
      </div>

      {/* Notifications, Settings, Help, Logout - Far right */}
      <div className="flex items-center space-x-2 text-dark-muted text-xs">
        {/* Force Desktop View Toggle - Only show if desktop view is forced */}
        {forceDesktopView && onToggleDesktopView && (
          <>
            <span
              onClick={onToggleDesktopView}
              className="flex items-center gap-1 hover:text-dark-text cursor-pointer bg-purple-900/30 border border-purple-900 text-purple-400 px-2 py-1 rounded animate-pulse"
              title="Click to switch to mobile view"
            >
              <Monitor size={12} />
              <span className="hidden sm:inline font-bold">Desktop View</span>
            </span>
            <div className="w-px h-4 bg-dark-border/30" />
          </>
        )}
        {/* Admin Editor Button */}
        <span
          onClick={() => setIsAdminEditorOpen(true)}
          className="flex items-center gap-1 hover:text-brand-400 cursor-pointer"
          aria-label="Open Admin Editor"
          title="Admin Editor"
          data-testid="admin-editor-button"
        >
          <Settings size={14} className="text-brand-500" />
        </span>
        <div className="w-px h-4 bg-dark-border/30" />
        {/* Notification Button */}
        <NotificationButton />
        {/* Dev Log Button */}
        <span
          onClick={() => setIsDevLogOpen(true)}
          className="flex items-center gap-1 hover:text-red-400 cursor-pointer"
          title="View error logs"
          data-testid="dev-log-button"
        >
          <Bug size={14} /> <span className="hidden sm:inline">Dev Log</span>
        </span>
        <div className="w-px h-4 bg-dark-border/30" />
        {/* AI Assistant Button */}
        <span
          onClick={onNavigateAssistant}
          className={`flex items-center gap-1 cursor-pointer rounded-full px-2 py-1 transition-colors ${
            isAssistantRoute
              ? 'bg-brand-400/15 text-brand-300 shadow-[inset_0_0_0_1px_rgba(55,221,240,0.24)]'
              : 'hover:bg-white/5 hover:text-brand-300'
          }`}
          title="Open AI Assistant"
          data-testid="ai-assistant-button"
        >
          <MessageCircle size={14} /> <span className="hidden sm:inline">AI</span>
        </span>
        <div className="w-px h-4 bg-dark-border/30" />
        <span onClick={onOpenSettings} className="flex items-center gap-1 hover:text-dark-text cursor-pointer">
          <Settings size={14} /> <span className="hidden sm:inline">Settings</span>
        </span>
        <span className="flex items-center gap-1 hover:text-dark-text cursor-pointer">
          <HelpCircle size={14} /> <span className="hidden sm:inline">Help</span>
        </span>
        <div className="w-px h-4 bg-dark-border/30" />
        <span
          onClick={onLogout}
          className="flex items-center gap-1 hover:text-red-400 cursor-pointer"
          data-testid="logout-button"
        >
          <LogOut size={14} /> <span className="hidden sm:inline">Logout</span>
        </span>
      </div>
    </div>

    {/* Admin Editor Modal */}
    <AdminEditorModal
      open={isAdminEditorOpen}
      onClose={() => setIsAdminEditorOpen(false)}
    />

    {/* Dev Log Panel */}
    <DevLogPanel
      open={isDevLogOpen}
      onClose={() => setIsDevLogOpen(false)}
    />
    </>
  );
};
